import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { type FormEvent, useState, useId } from 'react';
import { useMe } from '~/api/auth';
import { formError, isApiError } from '~/api/client';
import {
  type Department,
  departmentsQueryOptions,
  type Person,
  personQueryOptions,
  type UpdatePersonBody,
  useDisablePerson,
  useEnablePerson,
  useReinvitePerson,
  useUpdatePerson,
} from '~/api/people';
import { clockDaysQueryOptions, WORK_MODE_HINT } from '~/api/clock';
import { PROJECT_STATUS_LABEL, projectsQueryOptions } from '~/api/projects';
import { DaysTable } from '~/features/clock/DaysTable';
import { useServerClock } from '~/api/serverClock';
import { tasksQueryOptions } from '~/api/work';
import { PersonAnalysis } from '~/features/people/PersonAnalysis';
import { WorkModeSelect } from '~/features/people/WorkModeSelect';
import { STATUS_TONE as PROJECT_STATUS_TONE } from '~/features/projects/tones';
import { ProjectDot } from '~/features/projects/ProjectColor';
import {
  calendarDate,
  describeDue,
  describeSchedule,
  formatMinutes,
  ROLE_LABEL,
  STATUS_LABEL,
} from '~/lib/format';
import { notify } from '~/lib/notify';
import { Avatar } from '~/ui/Avatar';
import { Button } from '~/ui/Button';
import { useConfirm } from '~/ui/Confirm';
import { Dialog } from '~/ui/Dialog';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { InviteLinkOnce } from '~/ui/InviteLinkOnce';
import { Notice } from '~/ui/Notice';
import { Panel } from '~/ui/Panel';
import { Pill } from '~/ui/Pill';
import { STATUS_TONE } from '~/ui/tones';
import { Select } from '~/ui/Select';
import { Skeleton } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';

export const Route = createFileRoute('/_app/people/$id')({
  component: PersonPage,
});

function PersonPage() {
  const { id } = Route.useParams();
  const me = useMe();
  const person = useQuery(personQueryOptions(id));
  const departments = useQuery(departmentsQueryOptions);
  const isOwner = me.person.role === 'owner';

  if (person.isPending) {
    return (
      <div className="mx-auto max-w-[880px]">
        <Skeleton lines={5} />
      </div>
    );
  }
  if (person.isError) {
    const err = person.error;
    return (
      <div className="mx-auto max-w-[880px]">
        <BackLink />
        <ErrorNotice
          error={err}
          message={
            isApiError(err) && err.status === 403
              ? 'You can only open people in your own department.'
              : undefined
          }
          onRetry={() => person.refetch()}
        />
      </div>
    );
  }

  const p = person.data;
  const dept = departments.data?.find((d) => d.id === p.departmentId);
  const tz = p.tz ?? me.workspace.tz;
  const schedule = p.schedule ?? me.workspace.defaultSchedule;

  return (
    <div className="mx-auto max-w-[880px]">
      <BackLink />
      <header className="mb-6 flex flex-wrap items-center gap-4">
        <Avatar name={p.name} size="lg" personId={p.id} photoId={p.avatarFileId} viewable />
        <div className="min-w-0 flex-1">
          <h1 className="text-[26px] leading-tight">{p.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Pill>{ROLE_LABEL[p.role]}</Pill>
            <Pill tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Pill>
            {dept && <Pill tone="accent">{dept.name}</Pill>}
            {p.id === me.person.id && <Pill tone="accent">This is you</Pill>}
          </div>
        </div>
        {isOwner && (
          <OwnerActions
            person={p}
            departments={departments.data ?? []}
            isSelf={p.id === me.person.id}
          />
        )}
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Details">
          <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
            <dt className="text-ink-3">Email</dt>
            <dd className="font-mono text-xs leading-5">{p.email}</dd>
            <dt className="text-ink-3">Title</dt>
            <dd>{p.title ?? '—'}</dd>
            <dt className="text-ink-3">Employee code</dt>
            <dd className="font-mono">{p.empCode ?? '—'}</dd>
            <dt className="text-ink-3">Department</dt>
            <dd>{dept?.name ?? 'None'}</dd>
          </dl>
        </Panel>
        <Panel title="Working week">
          <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
            <dt className="text-ink-3">Schedule</dt>
            <dd>
              {describeSchedule(schedule)}
              {!p.schedule && <span className="ml-1 text-ink-3">(company default)</span>}
            </dd>
            <dt className="text-ink-3">Time zone</dt>
            <dd>
              {tz}
              {!p.tz && <span className="ml-1 text-ink-3">(company default)</span>}
            </dd>
            <dt className="text-ink-3">Clock</dt>
            <dd>{p.clockRequired ? 'Expected to clock in' : 'Not expected to clock in'}</dd>
            <dt className="text-ink-3">Daily report</dt>
            <dd>{p.reportRequired ? 'Expected on working days' : 'Not expected'}</dd>
            <dt className="text-ink-3">Works from</dt>
            <dd>{WORK_MODE_HINT[p.workMode]}</dd>
          </dl>
        </Panel>
      </div>
      <PersonWork personId={p.id} tz={tz} />
      {(p.id === me.person.id || me.person.role !== 'member') && (
        <PersonClock personId={p.id} tz={tz} />
      )}
      {me.person.role !== 'member' && (
        <div className="mt-6">
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ink-3">Analysis</h2>
          <PersonAnalysis personId={p.id} />
        </div>
      )}
    </div>
  );
}

/** What this person is on: their projects and their open one-time work, oldest first. */
function PersonWork({ personId, tz }: { personId: string; tz: string }) {
  const now = useServerClock(60_000);
  const today = calendarDate(now, tz);
  const projects = useQuery(projectsQueryOptions({ personId }));
  const tasks = useQuery(tasksQueryOptions({ ownerId: personId, status: 'open' }));
  const open = (tasks.data ?? []).filter((t) => !t.recurrence);
  const repeating = (tasks.data ?? []).filter((t) => t.recurrence).length;
  const live = (projects.data ?? []).filter((x) => x.status !== 'archived');

  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2">
      <Panel
        title="Projects"
        eyebrow={projects.data ? `${live.length} on the go or past` : undefined}
      >
        {projects.isPending ? (
          <Skeleton lines={3} />
        ) : projects.isError ? (
          <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
        ) : live.length === 0 ? (
          <p className="text-sm text-ink-3">Not on any project.</p>
        ) : (
          <ul className="divide-y divide-line">
            {live.map((x) => {
              const role = x.members.find((m) => m.personId === personId)?.role;
              return (
                <li key={x.id}>
                  <Link
                    to="/projects/$id"
                    params={{ id: x.id }}
                    className="flex items-center gap-2 py-2 text-sm hover:text-accent"
                  >
                    <span className="flex w-16 shrink-0 items-center gap-1.5 font-mono text-[11px] text-ink-2">
                      <ProjectDot color={x.color} className="size-2" />
                      {x.key}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{x.name}</span>
                    {role === 'lead' && <Pill tone="accent">Lead</Pill>}
                    <Pill tone={PROJECT_STATUS_TONE[x.status]}>
                      {PROJECT_STATUS_LABEL[x.status]}
                    </Pill>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      <Panel
        title="Open work"
        eyebrow={
          tasks.data
            ? `${open.length} one-time${repeating > 0 ? `, ${repeating} repeating` : ''}`
            : undefined
        }
      >
        {tasks.isPending ? (
          <Skeleton lines={3} />
        ) : tasks.isError ? (
          <ErrorNotice error={tasks.error} onRetry={() => tasks.refetch()} />
        ) : open.length === 0 ? (
          <p className="text-sm text-ink-3">Nothing open.</p>
        ) : (
          <ul className="divide-y divide-line">
            {open.slice(0, 12).map((t) => (
              <li key={t.id}>
                {t.projectId ? (
                  <Link
                    to="/projects/$id"
                    params={{ id: t.projectId }}
                    search={{ task: t.id }}
                    className="flex items-center gap-2 py-2 text-sm hover:text-accent"
                  >
                    <WorkRow
                      ref_={t.ref}
                      title={t.title}
                      due={t.dueDate}
                      budget={t.budgetMinutes}
                      today={today}
                    />
                  </Link>
                ) : (
                  <Link
                    to="/tasks"
                    search={{ view: 'list', filter: 'all', task: t.id }}
                    className="flex items-center gap-2 py-2 text-sm hover:text-accent"
                  >
                    <WorkRow
                      ref_={t.ref}
                      title={t.title}
                      due={t.dueDate}
                      budget={t.budgetMinutes}
                      today={today}
                    />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/** The person's work clock over the last two weeks. */
function PersonClock({ personId, tz }: { personId: string; tz: string }) {
  const me = useMe();
  const now = useServerClock(1000);
  const canReview =
    me.person.role === 'owner' || (me.person.role === 'manager' && personId !== me.person.id);
  const days = useQuery(clockDaysQueryOptions({ personId }));
  if (days.isError) return null;
  return (
    <div className="mt-4">
      <Panel title="Work clock" eyebrow="The last two weeks">
        {days.isPending ? (
          <Skeleton lines={4} />
        ) : (
          <DaysTable
            days={days.data.days}
            tz={tz}
            measuredAt={days.data.measuredAt}
            now={now}
            canReview={canReview}
          />
        )}
      </Panel>
    </div>
  );
}

function WorkRow({
  ref_,
  title,
  due,
  budget,
  today,
}: {
  ref_: string | null;
  title: string;
  due: string | null;
  budget: number | null;
  today: string;
}) {
  return (
    <>
      <span className="w-12 shrink-0 font-mono text-[11px] text-ink-3">{ref_ ?? ''}</span>
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {due && <span className="shrink-0 text-xs text-ink-3">{describeDue(due, today)}</span>}
      {budget !== null && (
        <span className="shrink-0 font-mono text-xs text-ink-3">{formatMinutes(budget)}</span>
      )}
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/people"
      className="mb-4 inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink"
    >
      <ArrowLeft size={16} aria-hidden /> People
    </Link>
  );
}

function OwnerActions({
  person,
  departments,
  isSelf,
}: {
  person: Person;
  departments: Department[];
  isSelf: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const reinvite = useReinvitePerson(person.id);
  const disable = useDisablePerson(person.id);
  const enable = useEnablePerson(person.id);
  const confirm = useConfirm();

  const onEnable = async () => {
    try {
      await enable.mutateAsync();
      notify.success('Account switched on. Send them a new invite link to sign in.');
    } catch (err) {
      notify.error('Could not switch the account on.', err);
    }
  };

  const onReinvite = async () => {
    const ok =
      person.status === 'active'
        ? await confirm({
            title: `Re-invite ${person.name}?`,
            body: 'Signs them out everywhere. If they use 11Deeds only with this company, their password is cleared and the new link sets a new one; if they also use it with another company, they keep their password and join again with it.',
            confirmLabel: 'Re-invite',
            tone: 'danger',
          })
        : await confirm({
            title: 'Send a new invite link?',
            body: 'The old one stops working.',
            confirmLabel: 'Send new link',
          });
    if (!ok) return;
    try {
      const res = await reinvite.mutateAsync();
      setInviteUrl(res.inviteUrl);
    } catch (err) {
      notify.error('Could not re-invite.', err);
    }
  };

  const onDisable = async () => {
    if (
      !(await confirm({
        title: `Switch off ${person.name}'s account?`,
        body: 'They are signed out everywhere and cannot sign in again until re-enabled.',
        confirmLabel: 'Switch off',
        tone: 'danger',
      }))
    )
      return;
    try {
      await disable.mutateAsync();
      notify.success('Account switched off.');
    } catch (err) {
      notify.error('Could not switch off the account.', err);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={() => setEditing(true)}>
        Edit
      </Button>
      {person.status !== 'disabled' && (
        <Button variant="secondary" onClick={onReinvite} loading={reinvite.isPending}>
          {person.status === 'invited' ? 'New invite link' : 'Re-invite'}
        </Button>
      )}
      {!isSelf && person.status !== 'disabled' && (
        <Button variant="danger" onClick={onDisable} loading={disable.isPending}>
          Switch off
        </Button>
      )}
      {person.status === 'disabled' && (
        <Button variant="secondary" onClick={onEnable} loading={enable.isPending}>
          Switch on
        </Button>
      )}
      <EditPersonDialog
        open={editing}
        onClose={() => setEditing(false)}
        person={person}
        departments={departments}
        isSelf={isSelf}
      />
      <Dialog
        open={inviteUrl !== null}
        onClose={() => setInviteUrl(null)}
        title="Send them this link."
        dismissible={false}
        footer={<Button onClick={() => setInviteUrl(null)}>Done</Button>}
      >
        {inviteUrl && <InviteLinkOnce url={inviteUrl} />}
      </Dialog>
    </div>
  );
}

function EditPersonDialog({
  open,
  onClose,
  person,
  departments,
  isSelf,
}: {
  open: boolean;
  onClose: () => void;
  person: Person;
  departments: Department[];
  isSelf: boolean;
}) {
  const update = useUpdatePerson(person.id);
  const [form, setForm] = useState<UpdatePersonBody>({
    name: person.name,
    title: person.title,
    empCode: person.empCode,
    // A guest's role comes from the Order Tracker, not from here.
    role: person.role === 'guest' ? undefined : person.role,
    departmentId: person.departmentId,
    clockRequired: person.clockRequired,
    reportRequired: person.reportRequired,
    workMode: person.workMode,
  });
  const { fields: fieldErrors, message: formMessage } = formError(update.error);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        ...form,
        title: form.title?.trim() || null,
        empCode: form.empCode?.trim() || null,
        departmentId: form.departmentId || null,
      });
      notify.success('Saved.');
      onClose();
    } catch {
      // shown inline
    }
  };

  // Unique per dialog: a page may hold several (one per card), all in the DOM.
  const editPersonId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="What changes?"
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={editPersonId} loading={update.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id={editPersonId} onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Name"
          value={form.name ?? ''}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldErrors['name']}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as UpdatePersonBody['role'] })}
            disabled={isSelf}
            hint={isSelf ? 'Make someone else the owner first.' : undefined}
          >
            <option value="member">Member</option>
            <option value="sub_manager">Sub manager</option>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </Select>
          <Select
            label="Department"
            value={form.departmentId ?? ''}
            onChange={(e) => setForm({ ...form, departmentId: e.target.value || null })}
          >
            <option value="">None</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Title"
            optional
            value={form.title ?? ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            error={fieldErrors['title']}
          />
          <Input
            label="Employee code"
            optional
            mono
            value={form.empCode ?? ''}
            onChange={(e) => setForm({ ...form, empCode: e.target.value })}
            error={fieldErrors['empCode']}
          />
        </div>
        <Switch
          label="Clock required"
          checked={form.clockRequired ?? true}
          onChange={(v) => setForm({ ...form, clockRequired: v })}
        />
        <Switch
          label="Daily report required"
          hint="Reminded at night when today's report is missing, and listed for their manager."
          checked={form.reportRequired ?? true}
          onChange={(v) => setForm({ ...form, reportRequired: v })}
        />
        <WorkModeSelect
          value={form.workMode ?? 'office'}
          onChange={(v) => setForm({ ...form, workMode: v })}
        />
        {formMessage && <Notice tone="late">{formMessage}</Notice>}
      </form>
    </Dialog>
  );
}
